import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { WeightedReturnType } from '../../../../features/performance/performance.domain';
import { UserPreferenceStrategyType } from '../../../../features/accounts/services/account-context.service';
import { InitialStateService } from '../../../../services/router/initial-state.service';
import { SecurityService } from '../../../../services/security.service';
import { PreferencesService } from '../../../../services/preferences/preferences.service';
import { UserPreferences } from '../../../../services/preferences/preferences.domain';
import { PreferencesType } from './customize-site.domain';

@Injectable()
export class CustomizeSiteService {
  private readonly RATE_OF_RETURN_TYPES: Array<WeightedReturnType> = [
    WeightedReturnType.TIME_WEIGHTED,
    WeightedReturnType.MONEY_WEIGHTED
  ];

  private readonly SITE_NAVIGATION_OPTIONS: Array<UserPreferenceStrategyType> = [
    UserPreferenceStrategyType.LAST_SELECTED,
    UserPreferenceStrategyType.DEFAULT_ACCOUNT
  ];

  private readonly MODAL_NAVIGATION_OPTIONS: Array<UserPreferenceStrategyType> = [
    UserPreferenceStrategyType.LAST_SELECTED,
    UserPreferenceStrategyType.DEFAULT_ACCOUNT,
    UserPreferenceStrategyType.ALWAYS_SELECT
  ];

  private readonly PREFERENCES_KEYS: Array<string> = [
    'WBT_START_PAGE',
    'ACCOUNT_ID',
    'SESSION_TIMEOUT',
    'ALLOWED_TIMEOUTS',
    'OPT_CHAIN_NUM_OF_STRIKES',
    'ACCOUNT_NAVIGATION_STRATEGY',
    'MODAL_ACCOUNT_NAVIGATION_STRATEGY',
    'PREFERRED_NAME'
  ];

  private DEFAULT_PREFERENCES: Partial<PreferencesType> = {
    timeout: 900,
    accountNavigationStrategy: this.SITE_NAVIGATION_OPTIONS[0],
    modalAccountNavigationStrategy: this.MODAL_NAVIGATION_OPTIONS[0],
    siteNavigationOptions: this.SITE_NAVIGATION_OPTIONS,
    modalNavigationOptions: this.MODAL_NAVIGATION_OPTIONS,
    preferredName: undefined
  };

  constructor(private securityService: SecurityService,
              private initialStateService: InitialStateService,
              private preferencesService: PreferencesService) {
  }

  /**
   * getPreferences
   * Assembles list of keys to retrieve from server and processes response into type
   *
   * @return
   */
  getPreferences(): Observable<PreferencesType> {
    if (this.securityService.hasPermission('PREFERENCES_RATE_OF_RETURN_TYPE')) {
      this.PREFERENCES_KEYS.push('WEIGHTED_RETURN_TYPE');
    }

    return this.preferencesService.getUserPreferences().pipe(
      map((result: UserPreferences): PreferencesType => this.mapPreferences(result))
    );
  }

  /**
   * savePreferences
   * Sends selected preferences to persist on server
   *
   * @param customizeSitePreferences
   * @return
   */
  savePreferences(customizeSitePreferences: PreferencesType): Observable<PreferencesType> {
    const preferences = {
      WBT_START_PAGE: customizeSitePreferences.startPage,
      ACCOUNT_ID: customizeSitePreferences.favoriteAccountId,
      SESSION_TIMEOUT: customizeSitePreferences.timeout,
      OPT_CHAIN_NUM_OF_STRIKES: customizeSitePreferences.numStrikes,
      ACCOUNT_NAVIGATION_STRATEGY: customizeSitePreferences.accountNavigationStrategy,
      MODAL_ACCOUNT_NAVIGATION_STRATEGY: customizeSitePreferences.modalAccountNavigationStrategy,
      PREFERRED_NAME: customizeSitePreferences.preferredName,
      WEIGHTED_RETURN_TYPE: undefined,
      SUGGESTED_FOR_YOU : customizeSitePreferences.sfuFlag,
      SHOW_CHAT: customizeSitePreferences.showChat
    };

    if (this.securityService.hasPermission('PREFERENCES_RATE_OF_RETURN_TYPE')) {
      preferences.WEIGHTED_RETURN_TYPE = customizeSitePreferences.rateOfReturnType;
    }
    // as per previous code, if we were caching accounts this would be the place to clear it
    // this.accountsRestService.clearGetAccountsCache();

    return this.preferencesService.updateUserPreferences(preferences).pipe(
      map((result: UserPreferences): PreferencesType => this.mapPreferences(result))
    );
  }

  private mapPreferences(result: UserPreferences): PreferencesType {
    const preferences = { ... this.DEFAULT_PREFERENCES } as PreferencesType;

    if (this.securityService.hasPermission('PREFERENCES_RATE_OF_RETURN_TYPE')) {
      preferences.rateOfReturnType =  this.RATE_OF_RETURN_TYPES[0]; // TODO : possible bug on original code !
      preferences.allowedRateOfReturnType = this.RATE_OF_RETURN_TYPES;
    }
    if (result.WBT_START_PAGE === undefined) {
      preferences.startPage = this.initialStateService.getInitialState().linkId;
    } else {
      preferences.startPage = result.WBT_START_PAGE as string;
    }
    if (this.hasSessionTimeoutValue(result)) {
      preferences.timeout = result.SESSION_TIMEOUT as number;
    }
    if (result.WEIGHTED_RETURN_TYPE !== undefined) {
      preferences.rateOfReturnType = result.WEIGHTED_RETURN_TYPE as WeightedReturnType;
    }
    if (result.ALLOWED_TIMEOUTS !== undefined) {
      preferences.allowedTimeouts = result.ALLOWED_TIMEOUTS as Array<number>;
    }
    if (result.OPT_CHAIN_NUM_OF_STRIKES !== undefined) {
      preferences.numStrikes = result.OPT_CHAIN_NUM_OF_STRIKES as string;
    }
    if (result.ACCOUNT_NAVIGATION_STRATEGY !== undefined) {
      preferences.accountNavigationStrategy = result.ACCOUNT_NAVIGATION_STRATEGY as UserPreferenceStrategyType;
    }
    if (result.MODAL_ACCOUNT_NAVIGATION_STRATEGY !== undefined) {
      preferences.modalAccountNavigationStrategy = result.MODAL_ACCOUNT_NAVIGATION_STRATEGY as UserPreferenceStrategyType;
    }
    if (result.PREFERRED_NAME !== undefined) {
      preferences.preferredName = result.PREFERRED_NAME as string;
    }
    if (result.ACCOUNT_ID !== undefined) {
      preferences.favoriteAccountId = result.ACCOUNT_ID as string; // TODO: we are actually getting account number here
    }
    if (result.SUGGESTED_FOR_YOU !== undefined) {
      preferences.sfuFlag = result.SUGGESTED_FOR_YOU as boolean;
    }
    if (result.SHOW_CHAT !== undefined) {
      preferences.showChat = result.SHOW_CHAT as boolean;
    }
    return preferences;
  }

  private hasSessionTimeoutValue(response: UserPreferences): boolean {
    if (response.SESSION_TIMEOUT !== undefined && response.ALLOWED_TIMEOUTS !== undefined) {
      return Boolean((response.ALLOWED_TIMEOUTS as Array<number>).find((value: number): boolean => value === response.SESSION_TIMEOUT));
    }
    return false;
  }
}

